import React, {
  FC,
  ReactElement,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { clamp } from 'utils/helpers/resourcesMap';
import styles from './index.module.scss';

interface PopupProps {
  content: ReactElement;
  elementBox: DOMRect | undefined;
  hidePopupCb: () => void;
  setBtnTextCb: (isOpen: boolean) => void;
}

const defaultPosition = { x: 0, y: 0 };
const defaultSize = { width: 0, height: 0 };

const Popup: FC<PopupProps> = ({
  content,
  elementBox,
  hidePopupCb,
  setBtnTextCb,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupSize, setPopupSize] = useState(defaultSize);

  useEffect(
    () =>
      setPopupSize(popupRef.current?.getBoundingClientRect() || defaultSize),
    [elementBox, popupRef]
  );

  const ElPosition = useMemo(() => {
    if (!elementBox) {
      return defaultPosition;
    }

    const offset = 30;

    const posX = elementBox.x + elementBox.width / 2 - popupSize.width / 2;
    const posY = elementBox.y + offset;

    const clampedX = clamp(posX, 0, window.innerWidth - popupSize.width);
    const clampedY = clamp(posY, 0, window.innerHeight - popupSize.height);

    return {
      x: clampedX,
      y: clampedY,
    };
  }, [elementBox, popupSize]);

  useEffect(() => {
    setBtnTextCb(!!elementBox);

    return () => {
      setBtnTextCb(false);
    };
  }, [elementBox, setBtnTextCb]);

  const opacity = useMemo(
    () => (Object.values(popupSize).some((v) => v === 0) ? 0 : 1),
    [popupSize]
  );

  if (!elementBox) {
    return null;
  }

  return (
    <div className={`popup-wrapper ${styles.popupWrapper}`}>
      <div
        role="button"
        tabIndex={0}
        className={`outsideClickLayer ${styles.outsideClickLayer}`}
        onClick={hidePopupCb}
        onKeyPress={() => null}
      >
        {' '}
      </div>
      <div
        ref={popupRef}
        className={`${styles.popup}`}
        style={{
          transform: `translate(${ElPosition.x}px, ${ElPosition.y}px)`,
          opacity,
        }}
      >
        {content}
      </div>
    </div>
  );
};

export default Popup;
